import { useLayoutEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5hierarchy from '@amcharts/amcharts5/hierarchy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import './Charts.scss';

interface SkillNode {
  name: string;
  value: number;
}

interface RootNode {
  name: string;
  value: number;
  children: SkillNode[];
}

type BubbleChartProps = {
  name: string;
  data: SkillNode[];
  height?: number;
  onClick?: (name: string) => void;
};

const BubbleChart: React.FC<BubbleChartProps> = ({ name, data, height = 400, onClick }) => {
  const chartId = `${name}-chart`;
  const rootRef = useRef<am5.Root | null>(null);

useLayoutEffect(() => {
  const root = am5.Root.new(chartId);
  rootRef.current = root;
  
  root.setThemes([am5themes_Animated.new(root)]);

const series = root.container.children.push(
  am5hierarchy.ForceDirected.new(root, {
    singleBranchOnly: false,
    downDepth: 1,
    initialDepth: 1,
    topDepth: 0,
    valueField: 'value',
    categoryField: 'name',
    childDataField: 'children',
    minRadius: 30,
    maxRadius: 70,
    nodePadding: 12,
    manyBodyStrength: -20,
    centerStrength: 0.8,
    linkWithField: ""
  })
);
series.links.template.setAll({
  strokeOpacity: 0,
  strokeWidth: 0,
});


  const nodeTemplate = series.circles.template;
  nodeTemplate.setAll({
    tooltipText: "{name}",
    cursorOverStyle: "pointer",
    fill: am5.color(0x043c5b), // Set all to dark blue
    strokeOpacity: 0,
  });

  nodeTemplate.events.on("click", (ev) => {
    const dataContext = ev.target.dataItem?.dataContext as SkillNode;
    if (dataContext?.name && onClick) onClick(dataContext.name);
  });

  series.labels.template.setAll({
    text: "{name}",
    fontSize: 13,
    fill: am5.color(0xffffff),
    centerX: am5.p50,
    centerY: am5.p50,
    populateText: true, 
  });
series.nodes.template.adapters.add("visible", (visible, target) => {
  const dataItem = target.dataItem?.dataContext as RootNode | SkillNode;
  if (dataItem?.name === "Skill Clusters") return false;
  return visible;
});
  const formattedData: RootNode[] = [
    {
      name: "Skill Clusters",
        value: 1,
      children: data
    },
  ];

  series.data.setAll(formattedData);
root.events.on("frameended", () => {
  series.dataItems.forEach((dataItem) => {
    const node = dataItem.get("node");
    const sprite = (node as any)?.get("sprite");

    if (sprite) {
      const radius = sprite.get("radius");
      const bounds = root.container.innerWidth();

      let x = sprite.x();
      if (x - radius < 0) sprite.set("x", radius);
      if (x + radius > bounds) sprite.set("x", bounds - radius);

      const height = root.container.innerHeight();
      let y = sprite.y();
      if (y - radius < 0) sprite.set("y", radius);
      if (y + radius > height) sprite.set("y", height - radius);
    }
  });
});

  
  return () => {
    root.dispose();
  };
}, [chartId, data, onClick]);

  return <div id={chartId} style={{ height }} className="chart-container" />;
};

export default BubbleChart;
